import React, { useState, useEffect, useContext } from 'react';
import { apiClient } from '../../api-wa';
import { getApiErrorMessage } from '../../api-wa/client';
import { AuthContext } from '../../Context/WaAuthContext';
import { ToastContext } from '../../Context/WaToastContext';
import { useConfig } from '../../Context/WaConfigContext';
import '../../styles-wa/whatsapp_config.css';

const DEFAULT_CONFIG = {
    name: 'Default WhatsApp',
    whatsapp_api_token: '',
    whatsapp_api_token_start_date: '',
    whatsapp_api_token_expire_date: '',
    phone_number_id: '',
    whatsapp_business_account_id: '',
    whatsapp_app_id: '',
    whatsapp_app_name: '',
    webhook_verify_token: 'my_secret_token_123',
    crm_webhook_url: 'http://localhost:8000/whatsapp/events/',
    crm_api_token: 'super_secret_crm_token_123',
    gemini_api_key: '',
    is_ai_enabled: false,
    chatbot_access: false,
    updated_at: ''
};

const normalizeConfig = (data = {}) => ({
    ...DEFAULT_CONFIG,
    ...data,
    whatsapp_api_token_start_date: data.whatsapp_api_token_start_date ? data.whatsapp_api_token_start_date.split('T')[0] : '',
    whatsapp_api_token_expire_date: data.whatsapp_api_token_expire_date ? data.whatsapp_api_token_expire_date.split('T')[0] : '',
    is_ai_enabled: Boolean(data.is_ai_enabled),
    chatbot_access: Boolean(data.chatbot_access),
});

const WhatsappConfig = ({ setActiveView }) => {
    const { addToast } = useContext(ToastContext);
    const { configs, activeConfigId, switchConfig, fetchConfigs } = useConfig();
    const configLimit = parseInt(localStorage.getItem('configLimit') || '1', 10);
    const [selectedConfigId, setSelectedConfigId] = useState(activeConfigId || '');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [config, setConfig] = useState(DEFAULT_CONFIG);
    const [initialConfig, setInitialConfig] = useState(DEFAULT_CONFIG);
    const [verifying, setVerifying] = useState(false);

    // Meta Embedded Signup & Coexistence states
    const [metaConfig, setMetaConfig] = useState(null);
    const [onboardingMode, setOnboardingMode] = useState('coexistence');
    const [signupData, setSignupData] = useState({
        wabaId: null,
        phoneNumberId: null,
        phoneNumber: null,
        businessName: null,
        pin: '',
        loading: false,
        step: 'idle',
        configId: null
    });

    // Fetch Meta configuration on mount
    useEffect(() => {
        const getMetaConfig = async () => {
            console.log('[MetaOnboarding] Fetching meta-config from backend...');
            try {
                const response = await apiClient.get('/whatsapp/config/meta-config/');
                console.log('[MetaOnboarding] Fetched meta-config successfully:', response.data);
                setMetaConfig(response.data);
            } catch (error) {
                console.error('[MetaOnboarding] Error fetching Meta configuration:', error);
            }
        };
        getMetaConfig();
    }, []);

    // Initialize Facebook JS SDK dynamically
    useEffect(() => {
        if (metaConfig && metaConfig.appId) {
            console.log('[MetaOnboarding] metaConfig loaded, initializing SDK with appId:', metaConfig.appId);
            if (window.FB) {
                console.log('[MetaOnboarding] window.FB already exists, calling FB.init directly');
                try {
                    window.FB.init({
                        appId: metaConfig.appId,
                        cookie: true,
                        xfbml: true,
                        version: metaConfig.graphVersion || 'v21.0'
                    });
                } catch (err) {
                    console.error('[MetaOnboarding] Meta SDK Re-init error:', err);
                }
                return;
            }

            console.log('[MetaOnboarding] Defining window.fbAsyncInit and injecting sdk.js');
            window.fbAsyncInit = function() {
                console.log('[MetaOnboarding] window.fbAsyncInit triggered by loaded script');
                try {
                    window.FB.init({
                        appId: metaConfig.appId,
                        cookie: true,
                        xfbml: true,
                        version: metaConfig.graphVersion || 'v21.0'
                    });
                    console.log('[MetaOnboarding] window.FB initialized successfully');
                } catch (err) {
                    console.error('[MetaOnboarding] Meta SDK init error:', err);
                }
            };

            (function(d, s, id) {
                var js, fjs = d.getElementsByTagName(s)[0];
                if (d.getElementById(id)) {
                    console.log('[MetaOnboarding] Script tag with id facebook-jssdk already exists');
                    return;
                }
                js = d.createElement(s); js.id = id;
                js.src = "https://connect.facebook.net/en_US/sdk.js";
                fjs.parentNode.insertBefore(js, fjs);
                console.log('[MetaOnboarding] Injected facebook-jssdk script tag into DOM');
            }(document, 'script', 'facebook-jssdk'));
        } else {
            console.log('[MetaOnboarding] metaConfig or appId missing, skipping SDK init', metaConfig);
        }
    }, [metaConfig]);

    // Handle postMessage callbacks from Meta Signup Dialog
    useEffect(() => {
        const handleMetaMessage = (event) => {
            let host;
            try {
                host = new URL(event.origin).hostname;
            } catch {
                return;
            }
            if (!host.endsWith('facebook.com')) return;

            let data = event.data;
            if (typeof data === 'string') {
                try {
                    data = JSON.parse(data);
                } catch {
                    return;
                }
            }
            console.log('[MetaOnboarding] Received postMessage from Meta:', data);
            if (data?.type !== 'WA_EMBEDDED_SIGNUP') return;

            const ev = data.event;
            if (['FINISH', 'FINISH_ONLY_WABA', 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'].includes(ev)) {
                if (data.data) {
                    console.log('[MetaOnboarding] Signup completed event data:', data.data);
                    setSignupData(prev => ({
                        ...prev,
                        wabaId: data.data.waba_id || prev.wabaId,
                        phoneNumberId: data.data.phone_number_id || prev.phoneNumberId
                    }));
                }
            }
        };

        window.addEventListener('message', handleMetaMessage);
        return () => window.removeEventListener('message', handleMetaMessage);
    }, []);

    const launchMetaSignup = () => {
        console.log('[MetaOnboarding] launchMetaSignup called. metaConfig:', metaConfig, 'window.FB:', !!window.FB);
        if (!metaConfig || !metaConfig.appId) {
            addToast('Meta Embedded Signup configuration is missing on the server.', 'error');
            return;
        }
        if (!window.FB) {
            addToast('Meta SDK is loading. Please try again in a few seconds.', 'warning');
            return;
        }

        // ES v4 reads product configuration (e.g. WhatsApp Business App onboarding /
        // coexistence) from the Login Configuration itself, not from extras - so the
        // mode is selected by which config_id we launch with, not an extras flag.
        const configId = onboardingMode === 'coexistence'
            ? (metaConfig.coexistenceConfigId || metaConfig.configId)
            : metaConfig.configId;

        console.log('[MetaOnboarding] Triggering FB.login with configId:', configId, 'mode:', onboardingMode);
        setSignupData(prev => ({ ...prev, step: 'authenticating', loading: true }));

        window.FB.login(
            (response) => {
                console.log('[MetaOnboarding] FB.login response received:', response);
                if (response.authResponse && response.authResponse.code) {
                    exchangeMetaCode(response.authResponse.code);
                } else {
                    setSignupData(prev => ({ ...prev, step: 'idle', loading: false }));
                    addToast('Meta authentication cancelled or did not return code.', 'warning');
                }
            },
            {
                config_id: configId,
                response_type: 'code',
                override_default_response_type: true,
                extras: {},
            }
        );
    };

    const exchangeMetaCode = async (code) => {
        setSignupData(prev => ({ ...prev, step: 'exchanging', loading: true }));
        try {
            const response = await apiClient.post('/whatsapp/config/exchange-token/', {
                code: code,
                config_id: selectedConfigId,
                wabaId: signupData.wabaId,
                phoneNumberId: signupData.phoneNumberId
            });

            if (response.data && response.data.success) {
                const configResult = response.data.config;
                const normalized = normalizeConfig(configResult);
                setConfig(normalized);
                setInitialConfig(normalized);

                setSignupData(prev => ({
                    ...prev,
                    step: 'pin_entry',
                    loading: false,
                    configId: configResult.id,
                    wabaId: response.data.wabaIds?.[0] || prev.wabaId,
                    phoneNumberId: response.data.phoneNumberId || prev.phoneNumberId,
                    phoneNumber: response.data.phoneNumber,
                    businessName: response.data.businessName
                }));
                addToast('Linked to Meta account successfully!', 'success');
            } else {
                throw new Error(response.data?.error || 'Token exchange failed');
            }
        } catch (error) {
            console.error('Error in exchangeMetaCode:', error);
            setSignupData(prev => ({ ...prev, step: 'idle', loading: false }));
            addToast(getApiErrorMessage(error, 'Token exchange failed'), 'error');
        }
    };

    const registerMetaPhone = async () => {
        if (!/^\d{6}$/.test(signupData.pin)) {
            addToast('PIN must be exactly 6 digits.', 'warning');
            return;
        }

        setSignupData(prev => ({ ...prev, step: 'registering', loading: true }));
        try {
            const response = await apiClient.post('/whatsapp/config/register-phone/', {
                config_id: signupData.configId,
                pin: signupData.pin
            });

            if (response.data && response.data.success) {
                addToast('Phone number successfully registered on Meta Cloud API!', 'success');
                setSignupData(prev => ({ ...prev, step: 'success', loading: false }));
                
                // Refresh list and select the newly registered config
                await fetchConfigs();
                switchConfig(signupData.configId);
                setSelectedConfigId(String(signupData.configId));
            } else {
                throw new Error(response.data?.error || 'Phone registration failed');
            }
        } catch (error) {
            console.error('Error in registerMetaPhone:', error);
            setSignupData(prev => ({ ...prev, step: 'pin_entry', loading: false }));
            addToast(getApiErrorMessage(error, 'Meta registration failed'), 'error');
        }
    };

    // Sync selectedConfigId with globally switched activeConfigId
    useEffect(() => {
        if (activeConfigId) {
            setSelectedConfigId(activeConfigId);
        } else if (configs && configs.length > 0) {
            setSelectedConfigId(String(configs[0].id));
        } else {
            setSelectedConfigId('new');
        }
    }, [activeConfigId, configs]);

    // Enforce configuration/app limit on selectedConfigId
    useEffect(() => {
        if (selectedConfigId === 'new' && configs && configs.length > 0 && configs.length >= configLimit) {
            const activeExists = configs.some(c => String(c.id) === String(activeConfigId));
            setSelectedConfigId(activeExists ? activeConfigId : String(configs[0].id));
            addToast('App limit reached. Upgrade your plan to connect more numbers.', 'warning');
        }
    }, [selectedConfigId, configs, activeConfigId, configLimit]);

    const fetchConfig = async () => {
        if (selectedConfigId === 'new' || !selectedConfigId) {
            const newConf = normalizeConfig({
                name: '',
                whatsapp_api_token: '',
                phone_number_id: '',
                whatsapp_business_account_id: '',
                whatsapp_app_id: '',
                whatsapp_app_name: '',
                webhook_verify_token: 'my_secret_token_123',
                crm_webhook_url: 'http://localhost:8000/whatsapp/events/',
                gemini_api_key: '',
                is_ai_enabled: false,
                chatbot_access: false
            });
            setConfig(newConf);
            setInitialConfig(newConf);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const response = await apiClient.get(`/whatsapp/config/${selectedConfigId}/`);
            const fetchedConf = normalizeConfig(response.data);
            setConfig(fetchedConf);
            setInitialConfig(fetchedConf);
        } catch (error) {
            console.error('Error fetching config:', error);
            addToast('Failed to load configuration', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchConfig();
    }, [selectedConfigId]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setConfig(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Check if anything changed
        if (JSON.stringify(config) === JSON.stringify(initialConfig)) {
            addToast('Nothing changed', 'info');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                ...config,
                whatsapp_api_token_start_date: config.whatsapp_api_token_start_date || null,
                whatsapp_api_token_expire_date: config.whatsapp_api_token_expire_date || null
            };

            let response;
            if (selectedConfigId === 'new') {
                // Create a new WhatsApp configuration row
                response = await apiClient.post('/whatsapp/config/', payload);
                const newConfig = response.data;
                addToast('Configuration created successfully', 'success');
                
                // Refresh global configs list & activate the new channel
                await fetchConfigs();
                switchConfig(newConfig.id);
                setSelectedConfigId(String(newConfig.id));
            } else {
                // Update existing configuration row
                const targetId = selectedConfigId || config.id;
                response = await apiClient.put(`/whatsapp/config/${targetId}/`, payload);
                const updatedConf = normalizeConfig(response.data);
                setConfig(updatedConf);
                setInitialConfig(updatedConf);
                addToast('Configuration updated successfully', 'success');
                
                // Refresh global configs list to sync names/details
                await fetchConfigs();
            }
        } catch (error) {
            console.error('Error saving config:', error);
            addToast(getApiErrorMessage(error, 'Failed to save configuration'), 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleVerifyStatus = async () => {
        if (selectedConfigId === 'new' || !config.id) return;
        setVerifying(true);
        try {
            const response = await apiClient.post(`/whatsapp/config/${config.id}/verify_status/`);
            const updatedConf = normalizeConfig(response.data);
            setConfig(updatedConf);
            setInitialConfig(updatedConf);
            addToast('Status refreshed successfully', 'success');
        } catch (error) {
            console.error('Error verifying status:', error);
            addToast('Failed to refresh status', 'error');
        } finally {
            setVerifying(false);
        }
    };

    const handleDelete = async () => {
        if (selectedConfigId === 'new') return;
        
        const targetId = selectedConfigId || config.id;
        if (!targetId) return;

        if (!window.confirm("Are you sure you want to delete this WhatsApp configuration channel? All campaigns, templates, messages, and isolated contacts linked exclusively to this configuration will be permanently affected.")) {
            return;
        }

        setDeleting(true);
        try {
            await apiClient.delete(`/whatsapp/config/${targetId}/`);
            addToast('Configuration deleted successfully', 'success');
            
            // Refresh configs list
            await fetchConfigs();
            
            // Re-fetch remaining configs from context
            const response = await apiClient.get('/whatsapp/config/');
            const remaining = response.data?.results ?? response.data ?? [];
            
            if (remaining.length > 0) {
                switchConfig(remaining[0].id);
                setSelectedConfigId(String(remaining[0].id));
            } else {
                setSelectedConfigId('new');
            }
        } catch (error) {
            console.error('Error deleting config:', error);
            addToast('Failed to delete configuration', 'error');
        } finally {
            setDeleting(false);
        }
    };

    if (loading) {
        return (
            <div className="config-loading">
                <div className="spinner"></div>
                <p>Loading configuration...</p>
            </div>
        );
    }

    return (
        <div className="config-container">
            <form onSubmit={handleSubmit} className="config-form">
                <div className="config-card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                        {selectedConfigId === 'new' ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                {configs.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => setSelectedConfigId(activeConfigId || '')}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: '1.2rem',
                                            color: 'var(--text-secondary, #64748b)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '4px',
                                            borderRadius: '4px'
                                        }}
                                        title="Go Back"
                                    >
                                        ←
                                    </button>
                                )}
                                <h3 className="card-title" style={{ margin: 0 }}>Connect New Number</h3>
                            </div>
                        ) : (
                            <>
                                <h3 className="card-title" style={{ margin: 0 }}>WhatsApp & Meta API</h3>
                                {configs.length < configLimit && (
                                    <button
                                        type="button"
                                        className="connect-number-btn"
                                        onClick={() => setSelectedConfigId('new')}
                                    >
                                        ➕ Connect New Number
                                    </button>
                                )}
                            </>
                        )}
                    </div>

                    {/* Meta Embedded Signup connection wizard card */}
                    <div className="meta-onboarding-wizard" style={{
                        background: 'linear-gradient(135deg, rgba(24, 119, 242, 0.05) 0%, rgba(34, 211, 238, 0.02) 100%)',
                        border: '1.5px solid rgba(24, 119, 242, 0.25)',
                        borderRadius: '12px',
                        padding: '20px',
                        marginBottom: '24px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                            <div style={{ 
                                width: '32px', 
                                height: '32px', 
                                borderRadius: '50%', 
                                background: '#1877F2', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '1.2rem'
                            }}>
                                f
                            </div>
                            <h4 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--text-dark)' }}>Meta Embedded Signup & Coexistence</h4>
                        </div>
                        
                        {signupData.step === 'idle' && (
                            <>
                                <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: 'var(--text-secondary, #64748b)', lineHeight: '1.5' }}>
                                    Quickly link your WhatsApp Business account via Meta. This will fetch and store your Phone Number ID, WhatsApp Business Account ID, App ID, and Access Token automatically.
                                </p>
                                
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
                                    <label style={{ display: 'flex', gap: '10px', cursor: 'pointer', background: 'var(--surface-soft-2)', padding: '12px', borderRadius: '8px', border: onboardingMode === 'coexistence' ? '1.5px solid var(--brand-purple)' : '1.5px solid transparent', alignItems: 'flex-start' }}>
                                        <input 
                                            type="radio" 
                                            name="onboarding-mode" 
                                            value="coexistence" 
                                            checked={onboardingMode === 'coexistence'} 
                                            onChange={() => setOnboardingMode('coexistence')} 
                                            style={{ marginTop: '3px' }}
                                        />
                                        <div>
                                            <strong style={{ display: 'block', fontSize: '13px', color: 'var(--text-dark)' }}>Coexistence Mode (Recommended)</strong>
                                            <span style={{ fontSize: '12px', color: 'var(--text-secondary, #64748b)' }}>Connects your existing WhatsApp Business mobile app seamlessly.</span>
                                        </div>
                                    </label>
                                    <label style={{ display: 'flex', gap: '10px', cursor: 'pointer', background: 'var(--surface-soft-2)', padding: '12px', borderRadius: '8px', border: onboardingMode === 'standard' ? '1.5px solid var(--brand-purple)' : '1.5px solid transparent', alignItems: 'flex-start' }}>
                                        <input 
                                            type="radio" 
                                            name="onboarding-mode" 
                                            value="standard" 
                                            checked={onboardingMode === 'standard'} 
                                            onChange={() => setOnboardingMode('standard')}
                                            style={{ marginTop: '3px' }}
                                        />
                                        <div>
                                            <strong style={{ display: 'block', fontSize: '13px', color: 'var(--text-dark)' }}>Standard Mode</strong>
                                            <span style={{ fontSize: '12px', color: 'var(--text-secondary, #64748b)' }}>Register a new number or migrate an existing one fully to the Meta Cloud API.</span>
                                        </div>
                                    </label>
                                </div>
                                
                                <button
                                    type="button"
                                    onClick={launchMetaSignup}
                                    style={{
                                        background: '#1877F2',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '10px 20px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        boxShadow: '0 2px 4px rgba(24, 119, 242, 0.2)'
                                    }}
                                >
                                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                                        <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm3.2 10.1h-2v6.6h-2.7v-6.6H9.3V9.5h1.2V7.9c0-1.7 1-2.6 2.6-2.6a14.2 14.2 0 0 1 1.6.1v1.8h-1.1c-.8 0-1 .4-1 1v1.3h2.1z"/>
                                    </svg>
                                    Continue with Facebook
                                </button>
                            </>
                        )}

                        {signupData.loading && (signupData.step === 'authenticating' || signupData.step === 'exchanging') && (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 0', gap: '12px' }}>
                                <div className="spinner" style={{ width: '30px', height: '30px' }}></div>
                                <span style={{ fontSize: '14px', color: 'var(--text-dark)', fontWeight: '600' }}>
                                    {signupData.step === 'authenticating' ? 'Awaiting Meta Authentication...' : 'Exchanging credentials...'}
                                </span>
                            </div>
                        )}

                        {signupData.step === 'pin_entry' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <div style={{ background: 'rgba(76, 175, 80, 0.1)', border: '1px solid rgba(76, 175, 80, 0.3)', borderRadius: '8px', padding: '12px', color: 'green', fontSize: '13px' }}>
                                    <strong>Account Connected!</strong> Connected &quot;{signupData.businessName}&quot; (Phone: {signupData.phoneNumber || '—'}).
                                </div>
                                <div className="input-group">
                                    <label style={{ fontWeight: '700', fontSize: '13px' }}>Enter 6-Digit WhatsApp PIN</label>
                                    <input 
                                        type="password"
                                        maxLength="6"
                                        placeholder="000000"
                                        value={signupData.pin}
                                        onChange={(e) => setSignupData(prev => ({ ...prev, pin: e.target.value.replace(/\D/g, '') }))}
                                        style={{ maxWidth: '150px', letterSpacing: '4px', textAlign: 'center', fontSize: '1.2rem', fontWeight: 'bold' }}
                                    />
                                    <span style={{ fontSize: '11px', color: 'var(--text-secondary, #64748b)' }}>Please enter the 6-digit registration PIN to verify the phone number on Meta Cloud API.</span>
                                </div>
                                <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                                    <button 
                                        type="button" 
                                        onClick={registerMetaPhone} 
                                        disabled={signupData.loading}
                                        style={{
                                            background: '#4CAF50',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '8px',
                                            padding: '8px 16px',
                                            fontWeight: '600',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {signupData.loading ? 'Registering...' : 'Register Phone Number'}
                                    </button>
                                    <button 
                                        type="button" 
                                        onClick={() => setSignupData(prev => ({ ...prev, step: 'idle' }))}
                                        style={{
                                            background: 'transparent',
                                            color: 'var(--text-gray)',
                                            border: '1px solid var(--border-color)',
                                            borderRadius: '8px',
                                            padding: '8px 16px',
                                            fontWeight: '600',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}

                        {signupData.step === 'success' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <div style={{ background: 'rgba(76, 175, 80, 0.1)', border: '1px solid rgba(76, 175, 80, 0.3)', borderRadius: '8px', padding: '12px', color: 'green', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span>✅</span>
                                    <strong>WhatsApp Number Registered Successfully!</strong>
                                </div>
                                <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: 'var(--text-secondary, #64748b)' }}>
                                    Your Meta Credentials are saved. You can now use the details generated below.
                                </p>
                                <button 
                                    type="button"
                                    onClick={() => setSignupData({ wabaId: null, phoneNumberId: null, pin: '', loading: false, step: 'idle' })}
                                    style={{
                                        alignSelf: 'flex-start',
                                        background: 'var(--brand-purple)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        padding: '6px 12px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Dismiss Wizard
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="input-grid">
                        <div className="input-group full-width">
                            <label>Configuration Account Name</label>
                            <input 
                                type="text" 
                                name="name" 
                                value={config.name || ''} 
                                onChange={handleInputChange}
                                placeholder="New WhatsApp Channel"
                                required
                                autoComplete="off"
                            />
                        </div>
                        <div className="input-group full-width">
                            <label>WhatsApp API Access Token</label>
                            <textarea 
                                name="whatsapp_api_token" 
                                value={config.whatsapp_api_token || ''} 
                                onChange={handleInputChange}
                                rows="3"
                                placeholder="Enter Meta System User Access Token"
                            />
                        </div>
                        <div className="input-group">
                            <label>Access Token Start Date</label>
                            <input
                                type="date"
                                name="whatsapp_api_token_start_date"
                                value={config.whatsapp_api_token_start_date || ''}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className="input-group">
                            <label>Access Token Expire Date</label>
                            <input
                                type="date"
                                name="whatsapp_api_token_expire_date"
                                value={config.whatsapp_api_token_expire_date || ''}
                                onChange={handleInputChange}
                                min={config.whatsapp_api_token_start_date || undefined}
                            />
                        </div>
                        <div className="input-group">
                            <label>Phone Number ID</label>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <input 
                                    type="text" 
                                    name="phone_number_id" 
                                    value={config.phone_number_id || ''} 
                                    onChange={handleInputChange}
                                    placeholder="e.g., 105647539824615"
                                    style={{ flex: 1 }}
                                />
                                {selectedConfigId !== 'new' && (
                                    <button
                                        type="button"
                                        onClick={handleVerifyStatus}
                                        disabled={verifying}
                                        title="Refresh Status"
                                        style={{
                                            background: 'var(--primary-light)',
                                            border: '1px solid var(--primary-color)',
                                            color: 'var(--primary-color)',
                                            padding: '8px',
                                            borderRadius: '6px',
                                            cursor: verifying ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        {verifying ? '⏳' : '🔄'}
                                    </button>
                                )}
                            </div>
                            {config.verification_status && selectedConfigId !== 'new' && (
                                <div style={{ marginTop: '8px', fontSize: '0.85rem' }}>
                                    <strong>Status: </strong> 
                                    <span style={{ 
                                        color: config.verification_status === 'VERIFIED' ? 'green' : 
                                               config.verification_status === 'UNKNOWN' ? 'orange' : 'red' 
                                    }}>
                                        {config.verification_status}
                                    </span>
                                    {config.last_checked_at && (
                                        <span style={{ marginLeft: '10px', color: '#666' }}>
                                            (Checked: {new Date(config.last_checked_at).toLocaleString()})
                                        </span>
                                    )}
                                </div>
                            )}
                            {config.error_message && selectedConfigId !== 'new' && (
                                <div style={{ marginTop: '5px', fontSize: '0.8rem', color: 'red' }}>
                                    Error: {config.error_message}
                                </div>
                            )}
                        </div>
                        <div className="input-group">
                            <label>Webhook Verify Token</label>
                            <input 
                                type="text" 
                                name="webhook_verify_token" 
                                value={config.webhook_verify_token || ''} 
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className="input-group">
                            <label>WhatsApp Business Account ID</label>
                            <input 
                                type="text" 
                                name="whatsapp_business_account_id" 
                                value={config.whatsapp_business_account_id || ''} 
                                onChange={handleInputChange}
                                placeholder="Enter Business Account ID"
                            />
                        </div>
                        <div className="input-group">
                            <label>WhatsApp App ID</label>
                            <input 
                                type="text" 
                                name="whatsapp_app_id" 
                                value={config.whatsapp_app_id || ''} 
                                onChange={handleInputChange}
                                placeholder="Enter Meta App ID"
                            />
                        </div>
                        <div className="input-group">
                            <label>WhatsApp App Name</label>
                            <input 
                                type="text" 
                                name="whatsapp_app_name" 
                                value={config.whatsapp_app_name || ''} 
                                onChange={handleInputChange}
                                placeholder="Enter Meta App Name"
                            />
                        </div>
                    </div>
                </div>

                <div className="config-card">
                    <h3 className="card-title">Webhook Integration</h3>
                    <div className="input-grid">
                        <div className="input-group">
                            <label>Webhook URL</label>
                            <input 
                                type="url" 
                                name="crm_webhook_url" 
                                value={config.crm_webhook_url || ''} 
                                onChange={handleInputChange}
                                placeholder="Webhook to receive Meta events"
                            />
                        </div>
                        <div className="input-group">
                            <label>API Token</label>
                            <input 
                                type="text" 
                                name="crm_api_token" 
                                value={config.crm_api_token || ''} 
                                onChange={handleInputChange}
                                placeholder="API Token for authentication"
                            />
                        </div>
                        <div className="input-group">
                            <label>Last Backend Update</label>
                            <input
                                type="text"
                                value={config.updated_at ? new Date(config.updated_at).toLocaleString() : 'Never'}
                                readOnly
                            />
                        </div>
                    </div>
                </div>

                <div className="config-card" style={{ marginTop: '24px' }}>
                    <h3 className="card-title">AI Auto-Reply</h3>
                    <div className="input-grid">
                        <div className="input-group full-width" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                            <div>
                                <label style={{ marginBottom: '4px' }}>Enable AI Auto-Reply</label>
                                <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary, #64748b)' }}>
                                    When on, incoming WhatsApp messages get an automatic AI-generated reply using your AI Config settings.
                                </p>
                            </div>
                            <div className="toggle-switch" style={{ flexShrink: 0 }}>
                                <input
                                    type="checkbox"
                                    id="is_ai_enabled"
                                    name="is_ai_enabled"
                                    checked={Boolean(config.is_ai_enabled)}
                                    onChange={handleInputChange}
                                />
                                <label htmlFor="is_ai_enabled"></label>
                            </div>
                        </div>
                    </div>
                </div>

                {config.chatbot_access && (
                    <div className="config-card" style={{ marginTop: '24px' }}>
                        <h3 className="card-title">AI Chatbot Settings (Gemini)</h3>
                        <div className="input-grid">
                            <div className="input-group full-width">
                                <label>Gemini API Key</label>
                                <input
                                    type="password"
                                    name="gemini_api_key"
                                    value={config.gemini_api_key || ''}
                                    onChange={handleInputChange}
                                    placeholder="Enter Google Gemini API Key"
                                />
                            </div>
                        </div>
                    </div>
                )}

                <div className="form-actions" style={{ display: 'flex', justifyContent: 'space-between', width: '100%', boxSizing: 'border-box' }}>
                    <div>
                        {selectedConfigId !== 'new' && configs.length > 1 && (
                            <button
                                type="button"
                                className="btn-cancel"
                                onClick={handleDelete}
                                disabled={deleting}
                                style={{
                                    borderColor: 'var(--text-error, #ef4444)',
                                    color: 'var(--text-error, #ef4444)',
                                }}
                            >
                                {deleting ? 'Deleting...' : '🗑 Delete Account'}
                            </button>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '12px' }}>
                        <button 
                            type="button" 
                            className="btn-cancel" 
                            onClick={() => {
                                if (selectedConfigId === 'new' && activeConfigId) {
                                    setSelectedConfigId(activeConfigId);
                                } else {
                                    fetchConfig();
                                }
                            }}
                        >
                            Cancel
                        </button>
                        <button type="submit" className="btn-submit" disabled={saving}>
                            {saving ? 'Saving...' : selectedConfigId === 'new' ? 'Connect Number' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default WhatsappConfig;



